import { t } from '../lib/i18n.js'
import type { MeetingView, RecipientView } from '@sorted/types'
import { useCallback, useEffect, useRef, useState } from 'react'
// `Joining` lives at the bottom of this file - the pinned-area path's whole
// recipient side, small enough that a separate module would be indirection.
import { ApiError, api } from '../lib/api.js'
import { Screen } from '../components/Screen.js'
import { Confirmed } from './Confirmed.js'
import { Origin } from './Origin.js'
import { Suggestion } from './Suggestion.js'
import { Waiting } from './Waiting.js'

/**
 * The meeting screen - routes by state, not by URL.
 *
 * The link is the source of truth (invariant 6): one URL shows whatever the
 * meeting currently needs from whoever opened it. No client-side routing to
 * get out of sync with the server.
 */

type Load =
  | { phase: 'loading' }
  | { phase: 'gone' }
  | { phase: 'error' }
  | { phase: 'ready'; view: MeetingView }

export function Meeting({ token }: { token: string }) {
  const [state, setState] = useState<Load>({ phase: 'loading' })

  const load = useCallback(async () => {
    try {
      setState({ phase: 'ready', view: await api.get(token) })
    } catch (err) {
      // Only a 404 means the link is actually gone. Collapsing every failure
      // into "expired" told people to ask for a new link when the real cause
      // was a server error - a lie that also hid the bug from us.
      const gone = err instanceof ApiError && err.status === 404
      setState(gone ? { phase: 'gone' } : { phase: 'error' })
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const view = state.phase === 'ready' ? state.view : null
  usePoll(load, shouldPoll(view))

  if (state.phase === 'loading') {
    return (
      <Screen centered>
        <span className="spinner spinner-page" />
      </Screen>
    )
  }

  if (state.phase === 'error') {
    // Something broke on our side. Say so, and offer the one useful action -
    // never a dead end (invariant 9), and never a wrong explanation.
    return (
      <Screen centered>
        <h1>{t('error.title')}</h1>
        <p className="muted">{t('error.hint')}</p>
        <button className="btn mt" onClick={() => void load()}>
          {t('error.retry')}
        </button>
      </Screen>
    )
  }

  if (state.phase === 'gone') {
    // Never a dead end (invariant 9) - an expired link still explains itself.
    return (
      <Screen centered>
        <h1>{t('gone.expired')}</h1>
        <p className="muted">{t('gone.expiredHint')}</p>
      </Screen>
    )
  }

  const v = state.view
  const update = (next: MeetingView) => setState({ phase: 'ready', view: next })

  if (v.status === 'CONFIRMED' || v.status === 'REMINDED') {
    return <Confirmed meeting={v} token={token} />
  }

  if (v.status === 'ABANDONED' || v.status === 'EXPIRED') {
    return (
      <Screen centered>
        <h1>{t(v.status === 'EXPIRED' ? 'gone.expired' : 'gone.abandoned')}</h1>
        <p className="muted">
          {v.status === 'EXPIRED'
            ? t('gone.expiredHint')
            : t('gone.abandonedHint')}
        </p>
      </Screen>
    )
  }

  // Alice waits - she never sees a venue before it is settled (invariant 3).
  if (v.role === 'creator') {
    return <Waiting meeting={v} token={token} onUpdate={update} />
  }

  const recipient = v as RecipientView

  if (v.status === 'CREATED' || v.status === 'AWAITING_ORIGIN') {
    // A pinned area asks him nothing: the area is already decided, so
    // where he is coming from changes neither the search nor the ranking.
    // Opening the link is the whole of his side - join, and the server has the
    // suggestions ready by the time it answers.
    return v.areaName ? (
      <Joining token={token} onResolved={update} />
    ) : (
      <Origin token={token} meeting={v} onResolved={update} />
    )
  }

  return <Suggestion token={token} meeting={recipient} onUpdate={update} />
}

/**
 * The pinned-area path's whole recipient side: join, then show what came back.
 *
 * No question, because there is nothing left to ask - the creator named the
 * area, so his origin would change neither the search nor the ranking. A
 * spinner rather than a screen, since it resolves in one request.
 */
function Joining({
  token,
  onResolved,
}: {
  token: string
  onResolved: (view: MeetingView) => void
}) {
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        await api.join(token)
        const view = await api.get(token)
        if (live) onResolved(view)
      } catch {
        // Never a dead end (invariant 9) - offer the retry rather than
        // spinning forever.
        if (live) setFailed(true)
      }
    })()
    return () => {
      live = false
    }
    // Runs once per token; onResolved is a fresh closure on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (failed) {
    return (
      <Screen centered>
        <h1>{t('error.title')}</h1>
        <p className="muted">{t('error.hint')}</p>
        <button className="btn mt" onClick={() => window.location.reload()}>
          {t('error.retry')}
        </button>
      </Screen>
    )
  }

  return (
    <Screen centered>
      <span className="spinner spinner-page" />
    </Screen>
  )
}

/**
 * Poll only while someone is actually waiting on the other party - spec §9.
 * "5s poll while tab open; no WebSockets needed."
 */
function shouldPoll(view: MeetingView | null): boolean {
  if (!view) return false
  if (view.role !== 'creator') return false
  return view.status !== 'CONFIRMED' && view.status !== 'REMINDED' && view.status !== 'ABANDONED'
}

function usePoll(fn: () => void | Promise<void>, active: boolean, ms = 5000) {
  const saved = useRef(fn)
  saved.current = fn

  useEffect(() => {
    if (!active) return

    // A background tab should not burn the user's battery or our database.
    const tick = () => {
      if (document.visibilityState === 'visible') void saved.current()
    }
    const id = setInterval(tick, ms)
    document.addEventListener('visibilitychange', tick)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [active, ms])
}
